/**
 * The History box (Synth VR, SRVR-0357): a dropdown whose field names the last edit and whose list shows
 * every edit the undo stack still holds, newest first. Display only — choosing a row does not move within
 * the history. The labels are the ones `pushUndo` gave each entry.
 *
 * Hovering a row, or the field (which stands for the newest entry), opens a panel beside the sidebar with
 * how many notes, walls and effects that entry affected (`historyDiff.ts`), found by comparing the entry's
 * snapshot with the next entry's, or with the current map for the newest. The panel is a portal so the
 * sidebar's scroll container cannot clip it, and it is computed on hover only.
 *
 * The list opens inline, under the field, for the same reason. Reads `undoStack` only; nothing here
 * subscribes to the playhead.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useEditorStore } from '../../store/editorStore';
import { countHistoryChanges, type HistoryCounts, type HistorySnapshot } from './historyDiff';
import {
  fieldStyle, fieldTextStyle, fieldEmptyStyle, arrowBtnStyle, listStyle, rowStyle,
  panelStyle, panelTitleStyle, panelLineStyle, panelEmptyStyle,
} from './HistoryBox.styles';

export const NO_EDITS_LABEL = 'No edits yet';
export const NO_CHANGES_LABEL = 'No object changes';

const COUNT_LINES: { key: keyof HistoryCounts; label: string }[] = [
  { key: 'notes', label: 'Notes' },
  { key: 'walls', label: 'Walls' },
  { key: 'effects', label: 'Effects' },
];

interface Hover { idx: number; top: number; left: number }

export default function HistoryBox() {
  const undoStack = useEditorStore(s => s.undoStack);
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState<Hover | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const empty = undoStack.length === 0;
  const shown = open && !empty;

  // Escape or a click outside closes the list.
  useEffect(() => {
    if (!shown) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [shown]);

  // A list that closed, or an entry that is gone (undo), takes its hover with it.
  const hoverIdx = hover && hover.idx < undoStack.length && (shown || hover.idx === undoStack.length - 1) ? hover.idx : null;

  const counts = useMemo<HistoryCounts | null>(() => {
    if (hoverIdx == null) return null;
    const st = useEditorStore.getState();
    if (!st.beatmap) return null;
    const after: HistorySnapshot = hoverIdx + 1 < undoStack.length
      ? undoStack[hoverIdx + 1]
      : { difficulties: st.beatmap.difficulties, flashEvents: st.flashEvents, effects: st.effects };
    return countHistoryChanges(undoStack[hoverIdx], after);
  }, [hoverIdx, undoStack]);

  const last = empty ? null : undoStack[undoStack.length - 1].label;

  /** Open the panel for entry `idx`, level with the hovered element and just right of the sidebar. */
  const enter = (idx: number) => (e: React.MouseEvent<HTMLElement>) => {
    const row = e.currentTarget.getBoundingClientRect();
    const side = rootRef.current?.getBoundingClientRect().right ?? row.right;
    setHover({ idx, top: Math.max(8, Math.min(row.top - 8, window.innerHeight * 0.4)), left: side + 14 });
  };
  const leave = () => setHover(null);

  return (
    <div ref={rootRef} data-testid="history-box">
      <div style={fieldStyle}>
        <div
          data-testid="history-field"
          style={empty ? fieldEmptyStyle : fieldTextStyle}
          onMouseEnter={empty ? undefined : enter(undoStack.length - 1)}
          onMouseLeave={leave}
        >
          {last ?? NO_EDITS_LABEL}
        </div>
        <button
          type="button"
          aria-label="Show edit history"
          aria-expanded={shown}
          disabled={empty}
          onClick={() => setOpen(o => !o)}
          style={arrowBtnStyle(shown, empty)}
        >
          {shown ? '▲' : '▼'}
        </button>
      </div>
      {shown && (
        <div data-testid="history-list" role="list" style={listStyle}>
          {undoStack.map((_, i) => {
            const idx = undoStack.length - 1 - i;   // newest first
            return (
              <div
                key={idx}
                role="listitem"
                data-testid="history-row"
                style={rowStyle(i === 0)}
                onMouseEnter={enter(idx)}
                onMouseLeave={leave}
              >
                {undoStack[idx].label}
              </div>
            );
          })}
        </div>
      )}
      {hoverIdx != null && counts && ReactDOM.createPortal(
        <div data-testid="history-panel" style={panelStyle(hover!.top, hover!.left)}>
          <div style={panelTitleStyle}>{undoStack[hoverIdx].label}</div>
          {COUNT_LINES.filter(c => counts[c.key] > 0).map(c => (
            <div key={c.key} data-testid={`history-${c.key}`} style={panelLineStyle}>{c.label}: {counts[c.key]}</div>
          ))}
          {COUNT_LINES.every(c => counts[c.key] === 0) && (
            <div data-testid="history-panel-empty" style={panelEmptyStyle}>{NO_CHANGES_LABEL}</div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
